import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react'
import { Icon, type IconName } from '@/components/ui/Icon'
import { Spinner } from '@/components/ui/Spinner'
import { cn } from '@/lib/cn'

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'destructive'
type ButtonSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl'

const variantClasses: Record<ButtonVariant, string> = {
  primary:
    'bg-primary-fill text-primary-on-fill hover:bg-primary-fill-hover active:bg-primary-fill-hover',
  secondary:
    'border border-border bg-surface text-ink hover:bg-surface-hover active:bg-surface-active',
  ghost: 'text-ink-secondary hover:bg-surface-hover hover:text-ink active:bg-surface-active',
  destructive:
    'bg-danger-fill text-danger-on-fill hover:bg-danger-fill-hover active:bg-danger-fill-hover',
}

const sizeClasses: Record<ButtonSize, string> = {
  xs: 'h-control-xs gap-1.5 px-3 text-sm',
  sm: 'h-control-sm gap-2 px-4 text-sm',
  md: 'h-control-md gap-2 px-6 text-base',
  lg: 'h-control-lg gap-2 px-7 text-base',
  xl: 'h-control-xl gap-2 px-8 text-base',
}

type ButtonProps = Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> & {
  variant?: ButtonVariant
  size?: ButtonSize
  icon?: IconName
  loading?: boolean
  children: ReactNode
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    icon,
    loading = false,
    disabled = false,
    className,
    children,
    type = 'button',
    ...rest
  },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(
        'inline-flex items-center justify-center rounded-full font-semibold whitespace-nowrap transition-colors duration-250 ease-brand',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring',
        'disabled:cursor-not-allowed disabled:border-transparent disabled:bg-surface-muted disabled:text-ink-disabled',
        'disabled:hover:bg-surface-muted disabled:hover:text-ink-disabled',
        variantClasses[variant],
        sizeClasses[size],
        className,
      )}
      {...rest}
    >
      {loading ? (
        <Spinner className="size-4" />
      ) : icon ? (
        <Icon name={icon} className="size-4" />
      ) : null}
      {children}
    </button>
  )
})
